import React, { useState, useRef, useEffect, useCallback } from "react";
import { PaletteContext } from "./palette_context";
import PaletteComponentStyle from "./palette_component_style";
import PaletteRow from "./palette_row";
import ActionButtons from "./action_buttons";
import { isProductPlaceholder, updateItem, preserveStableReferences } from "./palette_helpers";
import { NAME_KEY, SHOW_KEY, KIND_KEY, PRICE_KEY, KINDS } from "@/lib/printess/constants";
import { v4 as uuid } from 'uuid';
import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import usePaletteDnD from "./use_palette_dnd";

const hiddenKinds = [KINDS.META];

const Palette = ({ paletteItems, onItemsChanged }) => {
  const [items, setItems] = useState([...paletteItems]);
  const paletteContainerRef = useRef(null);
  const hasInitializedPlaceholdersRef = useRef(false);
  const isInitialized = useRef(false);

  const {
    sensors,
    draggingItem,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = usePaletteDnD(items, setItems);

  const productPlaceholder = () => ({
    [NAME_KEY]: "",
    [PRICE_KEY]: "",
    [KIND_KEY]: KINDS.PRODUCT,
    [SHOW_KEY]: false,
    _contracted: false,
    key: uuid(),
  });

  const itemsByCategory = () => {
    const groups = [];
    let currentGroup = [];

    for (const item of items) {
      // Start new groups from a category. But if we have a dangling product before categories,
      // start there too.
      if (item[KIND_KEY] === KINDS.CATEGORY || currentGroup.length === 0) {
        currentGroup = [item];
        groups.push(currentGroup);
      } else {
        currentGroup.push(item);
      }
    }
    return groups;
  };

  const ensureProductPlaceholders = () => {
    let changes = false;
    let newItems = [];

    itemsByCategory().forEach((group) => {
      const cleanedGroup = group.filter((item, index) => {
        // Keep only non-placeholders, except possibly the last item
        const isLast = index === group.length - 1;
        return !isProductPlaceholder(item) || isLast;
      });

      const lastItem = cleanedGroup[cleanedGroup.length - 1];

      if (!isProductPlaceholder(lastItem)) {
        changes = true;
        cleanedGroup.push(productPlaceholder());
      }

      newItems = [...newItems, ...cleanedGroup];
    });

    if (JSON.stringify(newItems) !== JSON.stringify(items)) {
      setItems((prev) => preserveStableReferences(prev, newItems));
      changes = true;
    }

    hasInitializedPlaceholdersRef.current = true;

    return changes;
  };

  const collapseAllAndSetInitialized = () => {
    const updated = items.map((item) => ({ ...item, _collapsed: true }));
    setItems(updated);
    isInitialized.current = true;
  };

  // Handle syncing with parent and placeholders
  useEffect(() => {
    // We want to make sure that placeholder rows are set up on the initially rendered items.
    // We take care to do this just once.
    if (!hasInitializedPlaceholdersRef.current) {
      const madeChanges = ensureProductPlaceholders();

      // No changes were made, so this useEffect won't be called again as a result
      // of setting the product placeholders. Make sure we're considered initialized.
      if (!madeChanges) { collapseAllAndSetInitialized();}
      return;
    }
    // ...This is coming from the product placeholders being set.
    if (!isInitialized.current) {
      collapseAllAndSetInitialized();
      return;
    }

    onItemsChanged(items);
    ensureProductPlaceholders();
  }, [items, onItemsChanged, ensureProductPlaceholders]);

  const handleSaveRow = useCallback((newItem) => {
    setItems((items) => updateItem(items, newItem));
  }, [setItems]);

  return (
    <PaletteContext.Provider value={{ items, setItems }}>
      <div ref={paletteContainerRef} className="palette-container">
        <PaletteComponentStyle />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="palette-rows">
            {itemsByCategory().map((group) => {
              const category = group[0];
              const products = group.slice(1).filter(
                (i) =>
                  i[KIND_KEY] === KINDS.PRODUCT &&
                  !hiddenKinds.includes(i[KIND_KEY]) &&
                  !isProductPlaceholder(i),
              );

              return (
                <div key={category.key} className="category-group">
                  <PaletteRow
                    item={category}
                    onSave={handleSaveRow}
                  />
                  <SortableContext
                    items={products.map((i) => i.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {group.slice(1).map(
                      (item) =>
                        !hiddenKinds.includes(item[KIND_KEY]) && (
                          <PaletteRow
                            key={item.key}
                            item={item}
                            onSave={handleSaveRow}
                          />
                        ),
                    )}
                  </SortableContext>
                </div>
              );
            })}
          </div>
          <DragOverlay>
            {draggingItem && (
              <PaletteRow key={draggingItem.key} item={draggingItem} />
            )}
          </DragOverlay>
        </DndContext>
        <ActionButtons setItems={setItems} />
      </div>
    </PaletteContext.Provider>
  );
};

export default Palette;
